// Secciones visuales del drill-down de un colaborador (tabla de registros,
// gráfico de horas/día, tendencia semanal, disponibilidad). Compartidas entre
// el modal de detalle de Admin (EmployeeDetailModal) y la vista de Colaborador
// (MyPerformance) para no duplicar la lógica de render.
import React, { useState } from 'react';
import type { SeedTimeEntry } from '../data/types';
import type { DayAvailability, AvailabilityStatus } from '../lib/employeeDetail';

// "2026-05-12" → "lun 12 may"
const DOW_ES = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MES_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function longDate(date: string): string {
  const d = new Date(date + 'T00:00:00');
  const [, m, day] = date.split('-');
  return `${DOW_ES[d.getDay()]} ${parseInt(day, 10)} ${MES_ES[parseInt(m, 10) - 1]}`;
}

// "2026-05-12" → "12-05"
function shortDate(date: string): string {
  const [, m, d] = date.split('-');
  return `${d}-${m}`;
}

export function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

// Color tri-estado, igual criterio que las celdas del reporte.
export function pctColor(v: number): string {
  if (v >= 1.0) return '#16a34a'; // verde
  if (v >= 0.8) return '#d97706'; // ámbar
  return '#dc2626'; // rojo
}

// ── Disponibilidad semanal (lun-vie) ──────────────────────────────────
const AVAIL_CONFIG: Record<
  AvailabilityStatus,
  { label: string; cls: string }
> = {
  disponible: { label: 'Disponible', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  vacaciones: { label: 'Vacaciones', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  licencia: { label: 'Licencia', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  administrativo: { label: 'Día adm.', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  feriado: { label: 'Feriado', cls: 'bg-red-100 text-red-800 border-red-200' },
  actividad_oficina: { label: 'Act. oficina', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

export function WeekAvailability({ days }: { days: DayAvailability[] }) {
  return (
    <section>
      <h4 className="font-semibold text-slate-900 mb-3">Disponibilidad semanal</h4>
      <div className="grid grid-cols-5 gap-2">
        {days.map((d) => {
          const cfg = AVAIL_CONFIG[d.status];
          return (
            <div
              key={d.date}
              className={`rounded-lg border p-2 text-center ${cfg.cls}`}
              title={d.detail || cfg.label}
            >
              <div className="text-xs font-semibold opacity-70">{d.weekdayLabel}</div>
              <div className="text-sm font-medium leading-tight mt-0.5">
                {cfg.label}
                {d.media && <span className="block text-[10px] font-normal">½ jornada</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Tabla de registros del período: Fecha · Cliente · Asunto · Descripción · Horas · Fact · Valor
export function EntriesTable({
  entries,
  periodLabel,
}: {
  entries: SeedTimeEntry[];
  periodLabel?: string;
}) {
  const totalHrs = entries.reduce((s, e) => s + e.durationHours, 0);
  // Solo se pueden sumar valores de la misma moneda; en la práctica casi todo es UF.
  const totalUF = entries
    .filter((e) => e.currency === 'UF')
    .reduce((s, e) => s + e.value, 0);

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="font-semibold text-slate-900">Detalle de registros</h4>
        <span className="text-xs text-slate-500">
          {periodLabel || 'período seleccionado'} · {entries.length} registros
        </span>
      </div>

      {entries.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center border border-slate-100 rounded-lg">
          Sin registros en este período.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-2.5 py-2 font-semibold whitespace-nowrap">Fecha</th>
                  <th className="text-left px-2.5 py-2 font-semibold">Cliente</th>
                  <th className="text-left px-2.5 py-2 font-semibold">Asunto</th>
                  <th className="text-left px-2.5 py-2 font-semibold">Descripción</th>
                  <th className="text-right px-2.5 py-2 font-semibold whitespace-nowrap">Hrs</th>
                  <th className="text-center px-2.5 py-2 font-semibold">Fact.</th>
                  <th className="text-right px-2.5 py-2 font-semibold whitespace-nowrap">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((e, i) => (
                  <tr key={i} className="hover:bg-slate-50 align-top">
                    <td className="px-2.5 py-1.5 whitespace-nowrap text-slate-500">
                      {shortDate(e.date)}
                    </td>
                    <td className="px-2.5 py-1.5 text-slate-900">{e.client || '—'}</td>
                    <td className="px-2.5 py-1.5 text-slate-600 max-w-[140px] truncate" title={e.matter}>
                      {e.matter || '—'}
                    </td>
                    <td className="px-2.5 py-1.5 text-slate-600 max-w-[220px] truncate" title={e.description}>
                      {e.description || '—'}
                    </td>
                    <td className="px-2.5 py-1.5 text-right whitespace-nowrap font-medium">
                      {e.durationHours.toFixed(1)}
                    </td>
                    <td className="px-2.5 py-1.5 text-center">
                      {e.billable ? (
                        <span className="text-fycom-green font-semibold">Sí</span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-right whitespace-nowrap text-slate-600">
                      {e.value > 0 ? `${e.currency} ${e.value.toFixed(1)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={4} className="px-2.5 py-2 font-semibold text-slate-700">
                    Total
                  </td>
                  <td className="px-2.5 py-2 text-right font-bold whitespace-nowrap">
                    {totalHrs.toFixed(1)}
                  </td>
                  <td />
                  <td className="px-2.5 py-2 text-right font-bold whitespace-nowrap">
                    UF {totalUF.toFixed(1)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

export function SummaryStat({
  label,
  value,
  color,
  sub,
}: {
  label: string;
  value: string;
  color: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="text-3xl font-bold" style={{ color }}>
        {value}
      </div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

// ── Tortas: Horas Totales por Cliente / Horas Facturables por Cliente ──
// Top 3 clientes (por horas totales del período) + "Otros" agrupando el resto.
// Mismo color por cliente en ambas tortas para poder comparar de un vistazo.
const PIE_COLORS = ['#3b82f6', '#16a34a', '#d97706', '#94a3b8'];
const OTROS_COLOR = '#cbd5e1';

interface ClientSlice {
  client: string;
  hours: number;
  color: string;
  // Solo presente en el slice "Otros": detalle de los clientes agrupados ahí.
  breakdown?: { client: string; hours: number }[];
}

function topClientsWithOthers(
  entries: SeedTimeEntry[],
  filterBillable?: boolean
): ClientSlice[] {
  const relevant = filterBillable === undefined ? entries : entries.filter((e) => e.billable === filterBillable);
  const byClient = new Map<string, number>();
  for (const e of relevant) {
    const key = e.client || 'Sin cliente';
    byClient.set(key, (byClient.get(key) ?? 0) + e.durationHours);
  }
  return Array.from(byClient.entries())
    .map(([client, hours]) => ({ client, hours, color: '' }))
    .sort((a, b) => b.hours - a.hours);
}

export function ClientHoursCharts({ entries }: { entries: SeedTimeEntry[] }) {
  // El TOP 3 se decide por horas TOTALES, y ambas tortas usan ese mismo orden
  // de clientes (para que el color de cada cliente sea igual en las dos).
  const totalRanking = topClientsWithOthers(entries);
  const top3Clients = totalRanking.slice(0, 3).map((s) => s.client);
  const colorByClient = new Map<string, string>();
  top3Clients.forEach((client, i) => colorByClient.set(client, PIE_COLORS[i]));

  function buildSlices(filterBillable?: boolean): ClientSlice[] {
    const ranking = topClientsWithOthers(entries, filterBillable);
    const top: ClientSlice[] = [];
    const otros: { client: string; hours: number }[] = [];
    for (const s of ranking) {
      if (top3Clients.includes(s.client)) {
        top.push({ ...s, color: colorByClient.get(s.client)! });
      } else {
        otros.push({ client: s.client, hours: s.hours });
      }
    }
    // Mantener el orden fijo del top3Clients (no el orden de "ranking" de este filtro).
    const ordered = top3Clients
      .map((c) => top.find((s) => s.client === c))
      .filter((s): s is ClientSlice => !!s);
    if (otros.length > 0) {
      const otrosHours = otros.reduce((sum, o) => sum + o.hours, 0);
      ordered.push({ client: 'Otros', hours: otrosHours, color: OTROS_COLOR, breakdown: otros });
    }
    return ordered;
  }

  const totalSlices = buildSlices();
  const billableSlices = buildSlices(true);
  const nonBillableSlices = buildSlices(false);

  if (totalSlices.length === 0) return null;

  return (
    <section>
      <h4 className="font-semibold text-slate-900 mb-3">Distribución por cliente</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <PieChartCard title="Horas Totales por Cliente" slices={totalSlices} />
        <PieChartCard title="Horas Facturables por Cliente" slices={billableSlices} />
        <PieChartCard title="Horas No Facturables por Cliente" slices={nonBillableSlices} />
      </div>
    </section>
  );
}

function PieChartCard({ title, slices }: { title: string; slices: ClientSlice[] }) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const total = slices.reduce((s, sl) => s + sl.hours, 0);

  if (total === 0) {
    return (
      <div className="border border-slate-200 rounded-xl p-4">
        <div className="text-sm font-medium text-slate-600 mb-3">{title}</div>
        <div className="text-sm text-slate-400 py-6 text-center">Sin horas en esta categoría.</div>
      </div>
    );
  }

  const R = 120;
  const CX = 140;
  const CY = 140;
  const LABEL_R = R * 0.65;

  let angleStart = -90; // empieza arriba (12 en punto)
  const arcs = slices.map((s, i) => {
    const fraction = s.hours / total;
    const angleEnd = angleStart + fraction * 360;
    const path = describeArc(CX, CY, R, angleStart, angleEnd);
    const midAngle = (angleStart + angleEnd) / 2;
    const labelPos = polarToCartesian(CX, CY, LABEL_R, midAngle);
    const result = { ...s, path, index: i, fraction, labelPos };
    angleStart = angleEnd;
    return result;
  });

  const hovered = hoverIndex !== null ? arcs[hoverIndex] : null;

  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="text-sm font-medium text-slate-600 mb-3">{title}</div>
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          {hovered?.breakdown && (
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full"
              style={{
                left: `${(hovered.labelPos.x / 280) * 100}%`,
                top: `${(hovered.labelPos.y / 280) * 100}%`,
                marginTop: '-8px',
              }}
            >
              <div className="bg-slate-800 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap max-w-[220px]">
                <div className="font-semibold mb-1">Otros ({pct(hovered.fraction)} · {hovered.hours.toFixed(1)}h)</div>
                <div className="space-y-0.5">
                  {hovered.breakdown
                    .slice()
                    .sort((a, b) => b.hours - a.hours)
                    .map((b) => (
                      <div key={b.client} className="flex justify-between gap-3">
                        <span className="truncate">{b.client}</span>
                        <span className="shrink-0">{b.hours.toFixed(1)}h</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="w-2 h-2 bg-slate-800 rotate-45 mx-auto -mt-1" />
            </div>
          )}

          <svg viewBox="0 0 280 280" className="w-56 h-56 shrink-0" role="img">
            {arcs.map((a) => (
              <g key={a.client}>
                <path
                  d={a.path}
                  fill={a.color}
                  stroke="white"
                  strokeWidth="2"
                  className="cursor-pointer transition-opacity"
                  opacity={hoverIndex === null || hoverIndex === a.index ? 1 : 0.5}
                  onMouseEnter={() => setHoverIndex(a.index)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  {!a.breakdown && (
                    <title>{`${a.client}: ${a.hours.toFixed(1)} hrs (${pct(a.fraction)})`}</title>
                  )}
                </path>
                {/* % dentro del slice, solo si es lo bastante grande para leerse */}
                {a.fraction >= 0.08 && (
                  <text
                    x={a.labelPos.x}
                    y={a.labelPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="16"
                    fontWeight="700"
                    fill="white"
                    className="pointer-events-none select-none"
                  >
                    {pct(a.fraction)}
                  </text>
                )}
              </g>
            ))}
          </svg>
        </div>
        <div className="w-full space-y-1.5 text-sm">
          {arcs.map((a) => (
            <div
              key={a.client}
              className={`flex items-center gap-2 cursor-pointer rounded px-1.5 py-1 -mx-1.5 ${
                hoverIndex === a.index ? 'bg-slate-100' : ''
              }`}
              onMouseEnter={() => setHoverIndex(a.index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <span
                className="inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: a.color }}
              />
              <span className="text-slate-700 truncate flex-1" title={a.client}>
                {a.client}
              </span>
              <span className="text-slate-500 shrink-0 font-medium">
                {pct(a.fraction)} · {a.hours.toFixed(1)}h
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

// Path de un slice de torta. Si el slice es 360° completo (un solo cliente),
// dibuja un círculo completo en vez de un arco degenerado.
function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  if (endAngle - startAngle >= 359.99) {
    return `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} Z`;
  }
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded-sm ${color}`} />
      {label}
    </span>
  );
}

// ── #2: barras apiladas de horas/día ──────────────────────────────────
export function DailyHoursChart({
  data,
  title,
}: {
  data: { date: string; billable: number; nonBillable: number }[];
  title?: string;
}) {
  const max = Math.max(8, ...data.map((d) => d.billable + d.nonBillable));
  const totalBill = data.reduce((s, d) => s + d.billable, 0);
  const totalNon = data.reduce((s, d) => s + d.nonBillable, 0);

  return (
    <section>
      <div className="mb-3">
        <h4 className="font-semibold text-slate-900">{title || 'Datos de los últimos 30 días:'}</h4>
        <h4 className="font-semibold text-slate-900">Horas por día</h4>
      </div>

      <div className="flex items-end gap-[3px] h-40 border-b border-slate-200">
        {data.map((d) => {
          const total = d.billable + d.nonBillable;
          const billH = (d.billable / max) * 100;
          const nonH = (d.nonBillable / max) * 100;
          const isWeekend = [0, 6].includes(new Date(d.date + 'T00:00:00').getDay());
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col justify-end h-full group relative cursor-default"
            >
              {/* Tooltip custom (aparece instantáneo al hover, sin delay del navegador) */}
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 w-max max-w-[180px]">
                <div className="bg-slate-800 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg">
                  <div className="font-semibold mb-0.5">{longDate(d.date)}</div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-sm bg-fycom-green" />
                    Facturable: <span className="font-medium">{d.billable.toFixed(1)} h</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-sm bg-slate-400" />
                    No facturable: <span className="font-medium">{d.nonBillable.toFixed(1)} h</span>
                  </div>
                  <div className="border-t border-slate-600 mt-1 pt-1">
                    Total: <span className="font-semibold">{total.toFixed(1)} h</span>
                  </div>
                </div>
                {/* flechita */}
                <div className="w-2 h-2 bg-slate-800 rotate-45 mx-auto -mt-1" />
              </div>

              <div
                className="w-full bg-fycom-green rounded-t-sm group-hover:brightness-110"
                style={{ height: `${billH}%` }}
              />
              <div
                className="w-full bg-slate-300 group-hover:brightness-105"
                style={{ height: `${nonH}%` }}
              />
              {total === 0 && (
                <div
                  className={`w-full ${isWeekend ? 'bg-slate-50' : 'bg-slate-100'}`}
                  style={{ height: '2px' }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-slate-600">
        <Legend color="bg-fycom-green" label={`Facturable (${totalBill.toFixed(0)} hrs)`} />
        <Legend color="bg-slate-300" label={`No facturable (${totalNon.toFixed(0)} hrs)`} />
      </div>
    </section>
  );
}

// ── #3: línea de tendencia semanal del % facturable ───────────────────
export function WeeklyTrendChart({
  data,
}: {
  data: { weekStart: string; label: string; pctBillable: number; expected: number; actual: number }[];
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const W = 640;
  const H = 160;
  const padL = 36;
  const padB = 28;
  const padT = 12;
  const padR = 12;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  // eje Y de 0% al máximo (mínimo 120%) para que el 100% no quede pegado al techo
  const maxPct = Math.max(1.2, ...data.map((d) => d.pctBillable));
  const x = (i: number) =>
    padL + (data.length <= 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
  const y = (v: number) => padT + plotH - (v / maxPct) * plotH;

  const linePath = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d.pctBillable).toFixed(1)}`)
    .join(' ');

  const y100 = y(1.0);
  const hovered = hoverIndex !== null ? data[hoverIndex] : null;

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="font-semibold text-slate-900">
          Tendencia de cumplimiento facturable
        </h4>
        <span className="text-xs text-slate-500">por semana</span>
      </div>

      <div className="relative">
        {hovered && (
          <div
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full"
            style={{
              left: `${(x(hoverIndex!) / W) * 100}%`,
              top: `${(y(hovered.pctBillable) / H) * 100}%`,
              marginTop: '-8px',
            }}
          >
            <div className="bg-slate-800 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
              <div className="font-semibold mb-0.5">Semana {hovered.label}</div>
              <div>
                Cumplimiento: <span className="font-medium">{pct(hovered.pctBillable)}</span>
              </div>
              <div>
                {hovered.actual.toFixed(1)} / {hovered.expected.toFixed(1)} hrs
              </div>
            </div>
            <div className="w-2 h-2 bg-slate-800 rotate-45 mx-auto -mt-1" />
          </div>
        )}

        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
          {/* línea de meta 100% */}
          <line
            x1={padL}
            x2={W - padR}
            y1={y100}
            y2={y100}
            stroke="#cbd5e1"
            strokeDasharray="4 4"
          />
          <text x={padL - 6} y={y100 + 3} textAnchor="end" fontSize="9" fill="#64748b">
            100%
          </text>
          <text x={padL - 6} y={padT + plotH + 3} textAnchor="end" fontSize="9" fill="#64748b">
            0%
          </text>

          {/* línea de tendencia */}
          <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth="2" />

          {/* puntos */}
          {data.map((d, i) => (
            <g key={d.weekStart}>
              {/* área de hover más grande que el punto visible, para que sea fácil de alcanzar */}
              <circle
                cx={x(i)}
                cy={y(d.pctBillable)}
                r="10"
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex((current) => (current === i ? null : current))}
              />
              <circle
                cx={x(i)}
                cy={y(d.pctBillable)}
                r={hoverIndex === i ? 5 : 3.5}
                fill={pctColor(d.pctBillable)}
                className="transition-all pointer-events-none"
              />
              {/* etiqueta de semana, una de cada dos para no saturar */}
              {i % 2 === 0 && (
                <text
                  x={x(i)}
                  y={H - 8}
                  textAnchor="middle"
                  fontSize="8"
                  fill="#94a3b8"
                >
                  {d.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
    </section>
  );
}
